import type { ApolloError } from "@apollo/client"
import type { PropsWithChildren, Dispatch, SetStateAction } from "react"
import React, { useState, useMemo, createContext, useContext } from "react"

import type {
    DisableQuotePaymentGroupMutationFn,
    EnableQuotePaymentGroupMutationFn,
    QuoteFieldsFragment,
    QuoteListingFieldsFragment,
} from "~graphql/generated/graphql"
import { PaymentPlanType } from "~graphql/generated/graphql"
import useCreateBooking from "~utils/use-create-booking"
import type { PaymentMethodState } from "~utils/use-payment-method-state"
import usePaymentMethodState from "~utils/use-payment-method-state"
import useQuote from "~utils/use-quote"
import { useCheckoutFlow } from "~components/pages/listing/checkout/checkout-flow/checkout-flow-provider"
import { navigateToBookingPage } from "~utils/navigation-helpers"

interface Props extends PropsWithChildren {
    readonly quoteId: string | null
}

interface BookTripContextValue {
    paymentPlanType: PaymentPlanType
    setPaymentPlanType: Dispatch<SetStateAction<PaymentPlanType>>
    paymentMethodState: PaymentMethodState
    updatePaymentMethodState: (update: Partial<PaymentMethodState>) => void
    createBooking: (paymentMethodState: PaymentMethodState) => Promise<void>
    quote: QuoteFieldsFragment | null
    listing: QuoteListingFieldsFragment | null
    isLoadingQuote: boolean
    isLoadingCreateBooking: boolean
    quoteError: ApolloError | undefined
    bookingError: ApolloError | undefined
    refetchQuote: () => void
    enableGroupPay: EnableQuotePaymentGroupMutationFn
    disableGroupPay: DisableQuotePaymentGroupMutationFn
}

const initialPaymentMethodState: PaymentMethodState = {
    paymentMethodId: null,
    isExternalPaymentMethod: false,
    isAchPaymentMethod: false,
}

const initialContextValue: BookTripContextValue = {
    paymentPlanType: PaymentPlanType.PayInFull,
    setPaymentPlanType: () => {},
    paymentMethodState: initialPaymentMethodState,
    updatePaymentMethodState: () => {},
    createBooking: () => Promise.resolve(),
    quote: null,
    listing: null,
    isLoadingQuote: false,
    isLoadingCreateBooking: false,
    quoteError: undefined,
    bookingError: undefined,
    refetchQuote: () => {},
    enableGroupPay: () => Promise.resolve({ data: undefined }),
    disableGroupPay: () => Promise.resolve({ data: undefined }),
}

const BookTripContext = createContext<BookTripContextValue>(initialContextValue)

export default function BookTripProvider({ quoteId, children }: Props) {
    const [paymentPlanType, setPaymentPlanType] = useState(
        PaymentPlanType.PayInFull
    )
    const { paymentMethodState, updatePaymentMethodState } =
        usePaymentMethodState()
    const { goToNextStep, widget, isWidget } = useCheckoutFlow()

    const useQuoteContext = useQuote({
        quoteId,
        paymentPlanType,
        paymentMethodState,
    })
    const onCreateBookingSuccess = (bookingId: string) =>
        isWidget
            ? goToNextStep({ booking_id: bookingId })
            : navigateToBookingPage(bookingId)

    const {
        createBooking,
        isLoading: isLoadingCreateBooking,
        error: bookingError,
    } = useCreateBooking({
        quote: useQuoteContext.quote,
        paymentPlanType,
        widget,
        onSuccess: onCreateBookingSuccess,
    })

    const contextValue = useMemo(
        () => ({
            paymentPlanType,
            setPaymentPlanType,
            paymentMethodState,
            updatePaymentMethodState,
            createBooking,
            isLoadingCreateBooking,
            bookingError,
            ...useQuoteContext,
        }),

        [
            paymentPlanType,
            paymentMethodState,
            isLoadingCreateBooking,
            bookingError,
            useQuoteContext,
        ]
    )

    return (
        <BookTripContext.Provider value={contextValue}>
            {children}
        </BookTripContext.Provider>
    )
}

export function useBookTrip() {
    return useContext(BookTripContext)
}
